import React, { useEffect, useRef, useState } from "react";
import { fetchQuery } from "../../utils/commonComponent/fetched";
import { useNavigate, useParams } from "react-router-dom";
import { getPermissions } from "../rbac/hasAccess";
import moment from "moment";
import { AddButton } from "../../utils/commonComponent/Custom/Button";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../global";
import { toast } from "react-toastify";


const InfoItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-md font-bold text-gray-800">{label}</span>
    <span className="text-gray-800">{value || "-"}</span>
  </div>
);

const formatDate = (value) => {
  if (!value || !moment(value).isValid()) return "-";
  return moment(value).format("DD/MM/YYYY");
};

const EmployeeView = (props) => {
  const params = useParams();
  const navigate = useNavigate(); 
  const permissions = getPermissions(props.moduleId);
  const [inputStates, setInputStates] = React.useState({});
  const [empList, setempList] = React.useState([]);
  console.log({ inputStates });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchEmployeesDetails = async () => {
    try {
      const [employee] = await fetchQuery(`select * from employees where id = ${params.id}`);

      const detailTables = [
        "employeeQualification",
        "employeeExperience",
        "employeeNominee",
        "employeeEmergencyDetails",
        "employeeOtherData",
        "employeeDocuments",
      ];

      const results = await Promise.all(
        detailTables.map((table) =>
          fetchQuery(`SELECT * FROM ${table} WHERE employeeId = ${params.id}`)
        )
      );

      const mergedData = { ...employee };

      detailTables.forEach((table, index) => {
        const record = results[index][0];
        if (record) {
          Object.entries(record).forEach(([key, value]) => {
            if (key === "id") {
              mergedData[`${table?.split("/")?.[1]}Id`] = value;
            } else if (!(key in mergedData)) {
              mergedData[key] = value;
            }
          });
        }
      });
      setInputStates(mergedData);
    } catch (error) {
      console.error("Error fetching employee details:", error);
    }
  };

  const allEmployeeList = async () => {
    try {
      const employees = await fetchQuery(`select id, employeesCode, name from employees`
      );
      setempList(employees);
    } catch (error) {
      console.error("Error fetching allEmployee list:", error);
    }
  };

  useEffect(() => {
    allEmployeeList();
  }, []);

  useEffect(() => {
    if (empList.length > 0) {
      fetchEmployeesDetails();
    }
  }, [empList]);

  // Get employee name from empList based on ID
  const getEmployeeNameById = (id) => {
    return empList?.find((emp) => emp.id == id)?.name || "";
  };

   // Navigate to the edit page
  const handleViewMore = () => {
    navigate(`/master/employeeMasterEdit/${params.id}`);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    // added validation only
    if (file && /[&#$*]/.test(file.name)) {
      toast.warning("File name contains special characters. Please rename the file and upload again.");
      return;
    }
    if (!file) return;
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert("Please upload an image file");
      return;
    }
    // Validate file size (e.g., max 5MB)
    if (file.size > 3 * 1024 * 1024) {
      alert("File size should be less than 3MB");
      return;
    }
    setIsUploading(true);

    try {
      const data = new FormData();
      data.append("fileToUpload", file);

      // Upload file
      const response = await axiosWithToken.post(BASE_URL + `fileUpload/fileUpload.php`, data);

      // Update employee table with image URL
      await axiosWithToken.patch(BASE_URL + `employees/${params.id}`,
        {
          profileImageUlr: response?.data || ""
        });

      // Update local state to show image immediately
      setInputStates(prev => ({
        ...prev,
        profileImageUlr: response?.data || ""
      }));
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getInitials = () => {
    return (inputStates?.name || "")
      ?.split(" ")
      ?.filter(Boolean)
      ?.slice(0, 2)
      .map((word) => word[0])
      ?.join("")
      .toUpperCase();
  };


  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* Card */}
      <div className="bg-white shadow-lg rounded-lg p-6 space-y-6">
        {/* Header: Profile */}
        <div className="flex items-center space-x-4">
          <div className="relative group">
            {/* Avatar Container */}
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-xl font-semibold text-gray-600 overflow-hidden">
              {inputStates?.profileImageUlr ? (
                <img 
                  src={inputStates.profileImageUlr} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{getInitials()}</span>
              )}
            </div>

            {/* Camera Overlay - appears on hover */}
            <div 
              onClick={triggerFileInput}
              className="absolute inset-0 w-16 h-16 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center cursor-pointer transition-all duration-200"
            >
              <svg 
                className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" 
                />
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" 
                />
              </svg>
            </div>

            {/* Loading Spinner */}
            {isUploading && (
              <div className="absolute inset-0 w-16 h-16 rounded-full bg-black bg-opacity-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            )}

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {inputStates?.name}
            </h2>
            <p className="text-sm text-gray-500">
              Employee Code: {inputStates?.employeesCode}
            </p>
          </div>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoItem label="Address" value={inputStates?.currentAddress} />
          <InfoItem label="Department" value={inputStates?.department} />
          <InfoItem label="Designation" value={inputStates?.hrDesignation} />
          <InfoItem label="Contact Number" value={inputStates?.contactNumber} />
          <InfoItem label="Personal Email" value={inputStates?.emailAddress} />
          <InfoItem
            label="Office Email"
            value={inputStates?.officeEmailAddress}
          />
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
          <InfoItem label="Date of Birth" value={formatDate(inputStates?.dateOfBirth)} />
          <InfoItem
            label="Date of Joining"
            value={formatDate(inputStates?.dateOfJoining)}
          />
          <InfoItem label="Gender" value={inputStates?.gender} />
        </div>

        {/* Reporting Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
          <InfoItem label="Reporting Manager" value={getEmployeeNameById(inputStates?.reportingManagerId)} />
          <InfoItem label="Approving Manager" value={getEmployeeNameById(inputStates?.appprovalManagerId)} />
        </div>

        {/* View More Button */}
        <div className="flex justify-end pt-4">
          <AddButton
            id="btn-add-outline"
            label=" View More +"
            onClick={handleViewMore}
          />
        </div>
      </div>
    </div>
  );
};

export default EmployeeView;